import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { FaBath, FaSearch, FaRulerCombined, FaHome } from "react-icons/fa";
import { GiBed } from "react-icons/gi";
import { Link } from "react-router-dom";
import safeHtml from "../../utils/safeHtml";
import fetchJsonWithFallback from "../../utils/fetchJsonWithFallback";
import { CDN_INCLUDES, CDN_ASSETS, propertyPhotoUrl } from "../../utils/cdn";
import localMainPageContent from "../../data/mainPage.json";
import localProperties from "../../data/properties.json";
import "./index.css";

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

const PRICE_RANGES = [
  { label: "Select price", value: "Any" },
  { label: "Under $1,500", value: "0-1500" },
  { label: "$1,500 - $2,000", value: "1500-2000" },
  { label: "$2,000 - $2,500", value: "2000-2500" },
  { label: "$2,500 - $3,000", value: "2500-3000" },
  { label: "$3,000+", value: "3000-Infinity" },
];

const COUNT_OPTIONS = ["Any", "1", "2", "3", "4+"];

function MainPage() {
  const [content, setContent] = useState(localMainPageContent);
  const [propertyData, setPropertyData] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [priceRange, setPriceRange] = useState("Any");
  const [bedrooms, setBedrooms] = useState("Any");
  const [bathrooms, setBathrooms] = useState("Any");
  const [pendingPrice, setPendingPrice] = useState("Any");
  const [pendingBedrooms, setPendingBedrooms] = useState("Any");
  const [pendingBathrooms, setPendingBathrooms] = useState("Any");

  useEffect(() => {
    let isMounted = true;

    fetchJsonWithFallback(CDN_INCLUDES + "mainPage.json", localMainPageContent).then((data) => {
      if (isMounted) setContent(data);
    });

    fetchJsonWithFallback(CDN_INCLUDES + "properties.json", localProperties).then((data) => {
      if (isMounted) {
        setPropertyData(data);
        setIsLoaded(true);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (window.location.hash === "#apply-filters") {
      const navbarHeight = document.querySelector(".site-navbar")?.offsetHeight || 80;
      const doScroll = () => {
        const el = document.getElementById("apply-filters");
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - navbarHeight;
          window.scrollTo({ top, behavior: "smooth" });
          window.history.replaceState(null, "", "/");
        }
      };
      const timerId = setTimeout(doScroll, 100);
      return () => clearTimeout(timerId);
    }
  }, []);

  const applyFilters = () => {
    setPriceRange(pendingPrice);
    setBedrooms(pendingBedrooms);
    setBathrooms(pendingBathrooms);
  };

  const matchesCount = (value, filter) => {
    if (filter === "Any") return true;
    if (filter === "4+") return Number(value) >= 4;
    return Number(value) === Number(filter);
  };

  const matchesPrice = (rent, filter) => {
    if (filter === "Any") return true;
    const [min, max] = filter.split("-").map(Number);
    return Number(rent) >= min && Number(rent) <= max;
  };

  const getFilteredProperties = () => {
    const searchTerm = search.trim().toLowerCase();

    return propertyData.filter((property) => {
      if (property.prop_visible === 0) return false;

      const { address, city, state, zipcode } = property.prop_location;
      const haystack = `${address} ${city} ${state} ${zipcode}`.toLowerCase();
      if (searchTerm && !haystack.includes(searchTerm)) return false;

      if (!matchesPrice(property.prop_rent, priceRange)) return false;
      if (!matchesCount(property.prop_amenities.bedroom, bedrooms)) return false;
      if (!matchesCount(property.prop_amenities.bathroom, bathrooms)) return false;

      return true;
    });
  };

  const filteredProperties = getFilteredProperties();

  return (
    <React.Fragment>
      <Container id="apply-filters" className="home-filter-bar">
        <Row className="align-items-end g-3">
          <Col xs={12} lg={4} className="home-filter-field home-filter-field--search">
            <label htmlFor="home-search">{safeHtml(content.searchLabel)}</label>
            <div className="home-filter-input">
              <FaSearch className="home-filter-input__icon" />
              <input
                id="home-search"
                type="text"
                placeholder={content.searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </Col>

          <Col xs={12} lg={2} className="home-filter-field">
            <label htmlFor="home-price">{safeHtml(content.monthlyRentLabel)}</label>
            <select id="home-price" value={pendingPrice} onChange={(e) => setPendingPrice(e.target.value)}>
              {PRICE_RANGES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Col>

          <Col xs={12} lg={2} className="home-filter-field">
            <label htmlFor="home-bathrooms">{safeHtml(content.bathroomsLabel)}</label>
            <select id="home-bathrooms" value={pendingBathrooms} onChange={(e) => setPendingBathrooms(e.target.value)}>
              {COUNT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </Col>

          <Col xs={12} lg={2} className="home-filter-field">
            <label htmlFor="home-bedrooms">{safeHtml(content.bedroomsLabel)}</label>
            <select id="home-bedrooms" value={pendingBedrooms} onChange={(e) => setPendingBedrooms(e.target.value)}>
              {COUNT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </Col>

          <Col xs={12} lg={2}>
            <button type="button" className="home-filter-apply" onClick={applyFilters}>
              {safeHtml(content.applyFiltersLabel)}
            </button>
          </Col>
        </Row>
      </Container>

      <Container className="home-results">
        {(filteredProperties.length > 0 || !isLoaded) && (
          <div className="home-results__heading-block">
            <p className="home-results__eyebrow">{safeHtml(content.eyebrow)}</p>
            <h2 className="home-results__heading">{safeHtml(content.heading)}</h2>
          </div>
        )}

        <Row className="g-4">
          {!isLoaded && (
            <Col xs={12}>
              <p className="home-results__empty">{safeHtml(content.loadingMessage)}</p>
            </Col>
          )}
          {isLoaded && filteredProperties.length === 0 && (
            <Col xs={12}>
              <p className="home-results__empty">{safeHtml(content.emptyResultsMessage)}</p>
            </Col>
          )}
          {filteredProperties.map((property) => {
            const address =
              (property.prop_location.address || "") +
              (property.prop_location.city ? ", " + property.prop_location.city : "") +
              (property.prop_location.state ? ", " + property.prop_location.state : "") +
              (property.prop_location.zipcode ? " " + property.prop_location.zipcode : "");

            return (
              <Col xs={12} lg={4} className="d-flex" key={property.prop_id}>
                <div className="property-card">
                  <div className="property-card__photo">
                    <img src={propertyPhotoUrl(property.prop_titlePhoto)} alt={address} />
                  </div>
                  <div className="property-card__details">
                    <div className="property-card__price-row">
                      <p className="property-card__price">
                        {formatter.format(property.prop_rent)} <span>/ month</span>
                      </p>
                      <span className="property-card__badge">{safeHtml(content.newListingBadgeLabel)}</span>
                    </div>
                    <p className="property-card__address">{safeHtml(address)}</p>
                    <hr className="property-card__divider" />
                    <div className="property-card__specs">
                      <span>
                        <GiBed /> {property.prop_amenities.bedroom} <em>Beds</em>
                      </span>
                      <span>
                        <FaBath /> {property.prop_amenities.bathroom} <em>Baths</em>
                      </span>
                      {property.prop_amenities.area !== "" && (
                        <span>
                          <FaRulerCombined /> {property.prop_amenities.area} <em>Sq. ft.</em>
                        </span>
                      )}
                      {property.prop_name !== "" && (
                        <span>
                          <FaHome /> <em>{safeHtml(property.prop_name)}</em>
                        </span>
                      )}
                    </div>
                    <div className="property-card__actions">
                      <a
                        className="property-card__cta property-card__cta--solid"
                        href={property.applyNowUrl && /^https?:\/\//.test(property.applyNowUrl) ? property.applyNowUrl : CDN_ASSETS + encodeURIComponent("Blank_Rental_Application.pdf")}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {safeHtml(content.applyNowLabel)}
                      </a>
                      <Link to={`/Property/${property.prop_id}`} className="property-card__cta property-card__cta--outline">
                        {safeHtml(content.viewDetailsLabel)}
                      </Link>
                    </div>
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      </Container>

      <Container className="home-map">
        <div className="home-map__heading-block">
          <h2 className="home-map__heading">{safeHtml(content.mapHeading)}</h2>
        </div>
        <Row className="g-0">
          <Col xs={12} className="home-map__frame">
            <iframe
              title="Service area map"
              src="https://www.google.com/maps?ll=33.8617,-118.0653&z=12&output=embed"
              loading="lazy"
            />
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
}

export default MainPage;
